import { useCallback, useRef, useState } from "react";

import { Sentry } from "@/lib/sentry";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";
import { getVoiceChatReportingSnapshot } from "@/hooks/useVoiceChatReducer";

export function useBugReport() {
  const { pb, user } = usePocketBase();
  const location = useLocation();
  const { t } = useTranslation(["common"]);
  const { session } = usePocketBase();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const screenshotRef = useRef<Blob | null>(null);

  const captureScreenshot = useCallback(async (): Promise<Blob | null> => {
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        ignoreElements: (element) =>
          element.getAttribute("data-bug-report") === "true",
      });
      return new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png", 0.8);
      });
    } catch (error) {
      console.error("Screenshot capture failed:", error);
      return null;
    }
  }, []);

  const submitToSentry = useCallback(
    (description: string): string | null => {
      try {
        const eventId = Sentry.captureMessage(
          `Bug Report: ${description.substring(0, 100)}`,
          {
            level: "info",
            tags: {
              type: "user_bug_report",
              route: location.pathname,
            },
            contexts: {
              bug_report: {
                description,
                url: window.location.href,
                userId: user?.id,
                hasScreenshot: !!screenshotRef.current,
              },
            },
          },
        );
        return eventId;
      } catch (error) {
        console.error("Sentry submission failed:", error);
        return null;
      }
    },
    [location.pathname, user],
  );

  const submitToPocketBase = useCallback(
    async (
      description: string,
      screenshot: Blob | null,
      sentryEventId: string | null,
    ) => {
      const formData = new FormData();
      formData.append("user", user?.id || "");
      formData.append("description", description);
      formData.append("url", window.location.href);
      if (sentryEventId) {
        formData.append("sentry_event_id", sentryEventId);
      }
      formData.append(
        "browser_info",
        JSON.stringify({
          userAgent: navigator.userAgent,
          language: navigator.language,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        }),
      );
      const { state: voiceChatState, lastAction: lastVoiceChatAction } =
        getVoiceChatReportingSnapshot();
      formData.append(
        "metadata",
        JSON.stringify({
          route: location.pathname,
          timestamp: new Date().toISOString(),
          sessionId: session,
          voiceChatState,
          lastVoiceChatAction,
        }),
      );
      if (screenshot) {
        formData.append("screenshot", screenshot, "screenshot.png");
      }

      await pb.collection("bug_reports").create(formData);
    },
    [pb, user, location.pathname],
  );

  const prepareReport = useCallback(async () => {
    screenshotRef.current = await captureScreenshot();
  }, [captureScreenshot]);

  const submitBugReport = useCallback(
    async (description: string) => {
      setIsSubmitting(true);
      try {
        const sentryEventId = submitToSentry(description);
        await submitToPocketBase(
          description,
          screenshotRef.current,
          sentryEventId,
        );

        toast.success(t("common:bugReport.successMessage"));
        screenshotRef.current = null;
        return true;
      } catch (error) {
        console.error("Bug report submission failed:", error);
        toast.error(t("common:bugReport.errorMessage"));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [submitToSentry, submitToPocketBase, t],
  );

  return {
    prepareReport,
    submitBugReport,
    isSubmitting,
  };
}
